import { ITableColumn } from "components/interfaces";
import { computed, makeObservable } from "mobx";
import DiscountController from "../api/controllers/DiscountController";
import { RequiredParamException } from "../api/exceptions/InvalidParamException";
import { GreaterThanOrEqualException } from "../api/exceptions/InvalidValueException";
import DiscountModel from "../api/models/DiscountModel";
import InstallmentsLinkModel from "../api/models/InstallmentsLinkModel";
import { lang } from "../i18nLang";
import { formatCode } from "../utils";
import { DiscountStore, MeasurementStore, MessageStore } from "./";

class Store {
  public readonly columns: ITableColumn[] = [
    {
      dataKey: "input",
      label: "",
      align: "center",
      width: 50,
    },
    {
      dataKey: "title",
      label: lang("fields.title"),
      width: 150,
    },
    {
      dataKey: "type",
      label: lang("fields.type"),
      width: 96,
    },
    /* Os valores fixos sao necessarios para manter a tabela alinhada */
    {
      dataKey: "total",
      label: lang("fields.value"),
      width: 150,
      monetary: true,
      align: "right",
    },
    {
      dataKey: "balance",
      label: lang("fields.balance"),
      width: 150,
      monetary: true,
      align: "right",
    },
    {
      dataKey: "maturityDate",
      label: lang("fields.maturity"),
      width: 96,
      align: "center",
    },
  ];

  public readonly subColumns: ITableColumn[] = [
    {
      dataKey: "input",
      label: "",
      width: 50,
      align: "right",
    },
    {
      dataKey: "title",
      label: "",
      width: 150,
    },
    {
      dataKey: "type",
      label: "",
      width: 96,
      align: "center",
    },
    {
      dataKey: "value",
      label: "",
      width: 150,
      monetary: true,
      align: "right",
    },
    {
      dataKey: "balance",
      label: "",
      width: 150,
      align: "center",
    },
    {
      dataKey: "maturityDate",
      label: "",
      width: 96,
      align: "center",
    },
  ];

  private _discountsLinks: InstallmentsLinkModel<DiscountModel>;

  constructor() {
    this._discountsLinks = new InstallmentsLinkModel<DiscountModel>();

    makeObservable(this, {
      rows: computed,
      discountsLinks: computed,
    });
  }

  get discountsLinks() {
    return this._discountsLinks.links;
  }

  get rows() {
    const measurementId = MeasurementStore.currentMeasurementUid;

    const formatInstallments = (installments) =>
      installments.map((installment) => {
        const title = lang(
          "fields.installmentNumber",
          `${installment.id}/${installments.length}`
        );
        const maturityDate =
          installment.maturityDate.toLocaleDateString("pt-br");

        const initialCheck = {
          checked: Boolean(installment.measurementId),
          disabled:
            installment.measurementId &&
            installment.measurementId !== measurementId,
        };

        return {
          ...installment,
          title,
          initialCheck,
          type: "-",
          balance: "-",
          maturityDate,
        };
      });

    return DiscountStore.discounts.map((discount) => ({
      id: discount.uid,
      code: formatCode(discount.code),
      title: discount.title,
      type: lang(`discounts.types.${discount.type}`),
      date: discount.getDate()?.toLocaleDateString("pt-br"),
      balance: discount.balanceWithoutMeasurement,
      total: discount.total,
      maturityDate: "-",
      installments: formatInstallments(discount.installments),
    }));
  }

  reset() {
    this._discountsLinks.clear();
  }

  getCurrentLinks() {
    const currentMeasurement = MeasurementStore.currentMeasurement;
    if (!currentMeasurement) return;

    const allDiscounts = [
      ...currentMeasurement.discounts,
      ...currentMeasurement.billings,
    ];

    allDiscounts.forEach((link) => {
      this.addItemToDiscountLink(link.discountId, link.installment);
    });
  }

  addItemToDiscountLink(discountId, installmentId) {
    const discountFound = this._discountsLinks.findLink(discountId);

    if (discountFound) {
      this._discountsLinks.addInstallmentId(discountFound, installmentId);
      return;
    }

    const discount = DiscountStore.findDiscount(discountId);
    if (discount) {
      this._discountsLinks.addLink(discount, installmentId);
    }
  }

  removeItemFromDiscountLink(discountId, installmentId) {
    this._discountsLinks.removeItem(discountId, installmentId);
  }

  updateDiscountsLinkWithMeasurement() {
    const measurement = MeasurementStore.currentMeasurement;
    if (!measurement) return;

    return DiscountController.linkDiscountsToMeasurement(
      measurement,
      this._discountsLinks.links
    )
      .then(() => {
        MessageStore.success(lang("discounts.success.linked"));
        return;
      })
      .catch((error) => {
        console.error(error);

        if (error instanceof RequiredParamException) {
          MessageStore.info(lang("discounts.error.noLink"));
        } else if (
          error instanceof GreaterThanOrEqualException &&
          error.field === "netTotal"
        ) {
          MessageStore.error(lang("measurements.error.invalidTotal.discounts"));
        } else {
          MessageStore.error(lang("discounts.error.onLinkMeasurement"));
        }
        return true;
      });
  }
}

const DiscountLinkStore = new Store();
export default DiscountLinkStore;
